import DraftIcon from '@/components/icons/draft';
import { mobileWidth } from '@/constants';
import { UserContext } from '@/contexts/userContext';
import AdvisorTableHeader from '@/modules/document/advisorTableHeader';
import {
  type VaultFileAccount,
  useGetVaultFileAccountsQuery,
} from '@/store/accountsApi';
import {
  Document2Record,
  useDownloadDocumentMutation,
  useGetDocuments2Query,
  usePreviewDocumentMutation,
} from '@/store/documentsApi';
import { useContext, useEffect, useMemo, useState } from 'react';
import { DocVaultGrid } from './DocVaultGrid';
import { VaultEmptyOverlay, VaultListState } from './VaultListState';
import { getQueriesToRetry, getVaultListStatus } from './vaultListStatus';

import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useShowTagSelectorDropdown } from '@/hooks/useShowTagSelectorDropdown';
import { useViewingTagSelection } from '@/hooks/useViewingTagSelection';
import { useModuleAccess } from '@/permissions';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid-premium';
import dayjs from '@/config/dayjs';
import { useLocalization } from '@/hooks/useLocalization';
import DocumentVaultTagEmptyState from './DocumentVaultTagEmptyState';
import { DataRangeFilterEnum } from '@/constants/dates';
import {
  getDocumentDownloadName,
  getDocumentFilenameForBlobHandling,
  hasActiveDocumentVaultTag,
  normalizeDocument2Records,
  normalizeVaultFileAccounts,
  shouldShowDocumentVaultTagEmptyState,
  skeletonRows,
} from './documentVaultGridUtils';
import {
  statementsVaultDateFilterOptions,
  toStatementsDateIntervalApiValue,
} from './statementsVaultGridConfig';
import {
  AdvisorFeedAccountSelect,
  AdvisorFeedDateSelect,
} from '@/modules/advisor/documentVault/AdvisorFeedFilters';
import {
  FEE_STATEMENT_CATEGORY_ID,
  PERFORMANCE_STATEMENT_CATEGORY_ID,
  STATEMENT_VAULT_CATEGORY_ID,
  advisorFeedDocuments2WealthAccountId,
  applyAdvisorFeedRows,
  formatFeedStatementDate,
  mergeFeedDocuments,
  statementCategoryIds,
  unionVaultAccounts,
} from '@/modules/advisor/documentVault/advisorFeedTab';

interface StatementsGridProps {
  searchTerm?: string;
  wealthAccountId?: number;
  vaultFileCategoryId?: number;
  isWidget?: boolean;
  investorId?: number;
  dateFilter?: DataRangeFilterEnum;
  onDateFilterChange?: (filter: DataRangeFilterEnum) => void;
  paginated?: boolean;
}

const StatementsGrid = ({
  searchTerm,
  wealthAccountId,
  vaultFileCategoryId,
  investorId,
  dateFilter: controlledDateFilter,
  onDateFilterChange,
  paginated = false,
}: StatementsGridProps) => {
  const { currentRole } = useContext(UserContext);
  const isAdvisor = (currentRole || '').toLowerCase() === 'advisor';
  const isAuthLoaded = useAuthIsLoaded();
  const { t } = useLocalization();
  const { validateByModule, validateByFeature } = useModuleAccess();
  const isMobile = useMediaQuery(mobileWidth);
  const showTagSelectorDropdown = useShowTagSelectorDropdown();
  const viewingTagSelection = useViewingTagSelection();
  const isAccountView = wealthAccountId !== undefined;
  const isAdvisorFeed = isAdvisor && !isAccountView;
  const showFeeAndPerformance =
    isAdvisorFeed &&
    validateByModule('DocumentVault2') &&
    validateByFeature('ShowPerf&FeeTabs');
  const hasSelectedViewingTag = hasActiveDocumentVaultTag(
    showTagSelectorDropdown,
    viewingTagSelection,
    investorId,
  );

  const columns: GridColDef<Document2Record>[] = useMemo(() => {
    const formatDate = (value: string | null | undefined) =>
      dayjs(value).isValid()
        ? dayjs(value).format('MM/DD/YYYY')
        : 'Invalid Date';
    const fileColumn: GridColDef<Document2Record> = {
      field: 'Name',
      headerName: t('grid.file'),
      flex: 1,
      renderCell: ({ row }) => (
        <Stack
          flexDirection='row'
          alignItems='center'
          gap={1}
        >
          <DraftIcon /> {row.Name}
        </Stack>
      ),
    };
    const lastUpdatedColumn: GridColDef<Document2Record> = {
      field: 'UploadDate',
      headerName: t('grid.lastUpdated'),
      width: 144,
      valueFormatter: formatDate,
    };
    const statementDateColumn: GridColDef<Document2Record> = {
      field: 'StatementDate',
      headerName: t('grid.statementDate'),
      width: 144,
      valueFormatter: isAdvisorFeed
        ? (value, row) => formatFeedStatementDate(value, row.UploadDate)
        : formatDate,
    };
    return isAdvisorFeed
      ? [fileColumn, statementDateColumn, lastUpdatedColumn]
      : [fileColumn, lastUpdatedColumn, statementDateColumn];
  }, [isAdvisorFeed, t]);

  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [selectedAccountIds, setSelectedAccountIds] = useState<number[]>([]);
  const [localDateFilter, setLocalDateFilter] = useState<DataRangeFilterEnum>(
    isAdvisorFeed
      ? DataRangeFilterEnum.Trailing1Year
      : DataRangeFilterEnum.Trailing3Months,
  );
  const dateFilter = controlledDateFilter ?? localDateFilter;
  const setDateFilter = (next: DataRangeFilterEnum) => {
    onDateFilterChange?.(next);
    if (controlledDateFilter === undefined) {
      setLocalDateFilter(next);
    }
  };

  const apiRef = useGridApiRef(); // Create apiRef

  const [previewDocument, { isLoading: isPreviewing }] =
    usePreviewDocumentMutation();
  const [downloadDocument, { isLoading: isDownloading }] =
    useDownloadDocumentMutation();

  const handleRowAction = async (
    action: 'preview' | 'download' | 'folderClick',
    data: Document2Record,
  ) => {
    // v2 documents don't have folders, so we only handle preview/download.
    // Trust the action from the child component, no need to re-evaluate.
    const statementId = (data as { StatementId?: string }).StatementId;

    if (action === 'preview') {
      await previewDocument({
        vaultId: data.VaultFile2Id,
        statementId,
        InvestorId: investorId,
        fileName: getDocumentFilenameForBlobHandling(data),
      }).unwrap();
      return;
    }

    if (action === 'download') {
      await downloadDocument({
        vaultId: data.VaultFile2Id,
        filename: getDocumentDownloadName(data),
        statementId,
        InvestorId: investorId,
      }).unwrap();
      return;
    }
  };

  const skipAccounts =
    !isAuthLoaded || isAccountView || (isAdvisor && !investorId);
  const skipDocuments =
    !isAuthLoaded || (isAdvisor && !isAccountView && !investorId);
  const accountQueryArgs = { advisorInvestorId: investorId };
  const statementCategories = statementCategoryIds(showFeeAndPerformance);
  const loadFeeStatements = statementCategories.includes(
    FEE_STATEMENT_CATEGORY_ID,
  );
  const loadPerformanceStatements = statementCategories.includes(
    PERFORMANCE_STATEMENT_CATEGORY_ID,
  );

  const isAccountsQuerySkipped = skipAccounts;
  const isDocumentsQuerySkipped = skipDocuments;
  const {
    currentData: accountsData,
    isLoading: isLoadingAccountsRaw,
    isFetching: isFetchingAccountsRaw,
    isError: isAccountsError,
    isUninitialized: isAccountsUninitialized,
    error: errorAccounts,
    refetch: refetchAccounts,
  } = useGetVaultFileAccountsQuery(
    {
      ...accountQueryArgs,
      vaultFileCategoryId: vaultFileCategoryId ?? STATEMENT_VAULT_CATEGORY_ID,
    },
    { skip: skipAccounts },
  );
  const {
    currentData: feeAccountsData,
    isLoading: isLoadingFeeAccountsRaw,
    isFetching: isFetchingFeeAccountsRaw,
    isError: isFeeAccountsError,
    isUninitialized: isFeeAccountsUninitialized,
    refetch: refetchFeeAccounts,
  } = useGetVaultFileAccountsQuery(
    {
      ...accountQueryArgs,
      vaultFileCategoryId: FEE_STATEMENT_CATEGORY_ID,
    },
    { skip: skipAccounts || !showFeeAndPerformance },
  );
  const {
    currentData: performanceAccountsData,
    isLoading: isLoadingPerformanceAccountsRaw,
    isFetching: isFetchingPerformanceAccountsRaw,
    isError: isPerformanceAccountsError,
    isUninitialized: isPerformanceAccountsUninitialized,
    refetch: refetchPerformanceAccounts,
  } = useGetVaultFileAccountsQuery(
    {
      ...accountQueryArgs,
      vaultFileCategoryId: PERFORMANCE_STATEMENT_CATEGORY_ID,
    },
    { skip: skipAccounts || !showFeeAndPerformance },
  );

  const vaultFileAccounts: VaultFileAccount[] = useMemo(() => {
    const statementAccounts = normalizeVaultFileAccounts(accountsData);
    if (!showFeeAndPerformance) {
      return statementAccounts;
    }
    return unionVaultAccounts([
      statementAccounts,
      normalizeVaultFileAccounts(feeAccountsData),
      normalizeVaultFileAccounts(performanceAccountsData),
    ]);
  }, [
    accountsData,
    feeAccountsData,
    performanceAccountsData,
    showFeeAndPerformance,
  ]);

  const isLoadingAccounts = Boolean(
    isLoadingAccountsRaw ||
    (showFeeAndPerformance &&
      (isLoadingFeeAccountsRaw || isLoadingPerformanceAccountsRaw)),
  );
  const skipDocumentsUntilAccountsReady =
    skipDocuments || (isAdvisorFeed && isLoadingAccounts);

  const documentQueryArgs = useMemo(
    () => ({
      DateInterval: toStatementsDateIntervalApiValue(dateFilter),
      WealthAccountId: advisorFeedDocuments2WealthAccountId({
        isAccountView,
        wealthAccountId,
        selectedAccount,
        selectedAccountIds,
      }),
      FileSourceId: undefined,
      SearchTerm: searchTerm,
      AdvisorInvestorId: investorId,
    }),
    [
      dateFilter,
      investorId,
      isAccountView,
      searchTerm,
      selectedAccount,
      selectedAccountIds,
      wealthAccountId,
    ],
  );

  const {
    currentData: statementsData,
    isLoading: isLoadingStatementsRaw,
    isFetching: isFetchingStatementsRaw,
    isError: isDocumentsError,
    isUninitialized: isDocumentsUninitialized,
    error: errorStatements,
    refetch: refetchDocuments,
  } = useGetDocuments2Query(
    {
      ...documentQueryArgs,
      VaultFileCategoryId: vaultFileCategoryId ?? STATEMENT_VAULT_CATEGORY_ID,
    },
    { skip: skipDocumentsUntilAccountsReady },
  );
  const {
    currentData: feeDocumentsData,
    isLoading: isLoadingFeeDocumentsRaw,
    isFetching: isFetchingFeeDocumentsRaw,
    isError: isFeeDocumentsError,
    isUninitialized: isFeeDocumentsUninitialized,
    error: errorFeeDocuments,
    refetch: refetchFeeDocuments,
  } = useGetDocuments2Query(
    {
      ...documentQueryArgs,
      VaultFileCategoryId: FEE_STATEMENT_CATEGORY_ID,
    },
    { skip: skipDocumentsUntilAccountsReady || !loadFeeStatements },
  );
  const {
    currentData: performanceDocumentsData,
    isLoading: isLoadingPerformanceDocumentsRaw,
    isFetching: isFetchingPerformanceDocumentsRaw,
    isError: isPerformanceDocumentsError,
    isUninitialized: isPerformanceDocumentsUninitialized,
    error: errorPerformanceDocuments,
    refetch: refetchPerformanceDocuments,
  } = useGetDocuments2Query(
    {
      ...documentQueryArgs,
      VaultFileCategoryId: PERFORMANCE_STATEMENT_CATEGORY_ID,
    },
    { skip: skipDocumentsUntilAccountsReady || !loadPerformanceStatements },
  );

  const handleAccountChange = (event: SelectChangeEvent) => {
    setSelectedAccount(event.target.value);
  };

  const handleDateFilterChange = (event: SelectChangeEvent) => {
    setDateFilter(event.target.value as DataRangeFilterEnum);
  };

  const rows = useMemo(() => {
    const statementRows = normalizeDocument2Records(statementsData);
    const merged = loadFeeStatements
      ? mergeFeedDocuments([
          statementRows,
          normalizeDocument2Records(feeDocumentsData),
          normalizeDocument2Records(performanceDocumentsData),
        ])
      : statementRows;
    if (!isAdvisorFeed) {
      return merged;
    }
    return applyAdvisorFeedRows(merged, dateFilter, selectedAccountIds);
  }, [
    dateFilter,
    feeDocumentsData,
    isAdvisorFeed,
    loadFeeStatements,
    performanceDocumentsData,
    selectedAccountIds,
    statementsData,
  ]);
  const isLoadingStatements = Boolean(
    isLoadingStatementsRaw ||
    (showFeeAndPerformance &&
      (isLoadingFeeDocumentsRaw || isLoadingPerformanceDocumentsRaw)),
  );
  const isFetchingStatements = Boolean(
    isFetchingStatementsRaw ||
    (showFeeAndPerformance &&
      (isFetchingFeeDocumentsRaw || isFetchingPerformanceDocumentsRaw)),
  );
  const isLoading = isLoadingAccounts || isLoadingStatements;
  const isFetching = isFetchingStatements;
  const hasDocumentLoadError = Boolean(
    errorStatements ??
    (showFeeAndPerformance && (errorFeeDocuments ?? errorPerformanceDocuments)),
  );
  const accountsQuery = {
    isLoading: isLoadingAccounts,
    isFetching: Boolean(isFetchingAccountsRaw),
    isError: Boolean(isAccountsError),
    isUninitialized: Boolean(isAccountsUninitialized),
    hasData: Boolean(accountsData),
    skip: isAccountsQuerySkipped,
  };
  const documentsQuery = {
    isLoading: isLoadingStatements,
    isFetching,
    isError: Boolean(isDocumentsError),
    isUninitialized: Boolean(isDocumentsUninitialized),
    hasData: Boolean(statementsData),
    skip: isDocumentsQuerySkipped || skipDocumentsUntilAccountsReady,
  };
  const feeDocumentsQuery = {
    isLoading: Boolean(isLoadingFeeDocumentsRaw),
    isFetching: Boolean(isFetchingFeeDocumentsRaw),
    isError: Boolean(isFeeDocumentsError),
    isUninitialized: Boolean(isFeeDocumentsUninitialized),
    hasData: Boolean(feeDocumentsData),
    skip: !loadFeeStatements || skipDocumentsUntilAccountsReady,
  };
  const performanceDocumentsQuery = {
    isLoading: Boolean(isLoadingPerformanceDocumentsRaw),
    isFetching: Boolean(isFetchingPerformanceDocumentsRaw),
    isError: Boolean(isPerformanceDocumentsError),
    isUninitialized: Boolean(isPerformanceDocumentsUninitialized),
    hasData: Boolean(performanceDocumentsData),
    skip: !loadPerformanceStatements || skipDocumentsUntilAccountsReady,
  };
  const documentQueries = [
    documentsQuery,
    feeDocumentsQuery,
    performanceDocumentsQuery,
  ];
  const feeAccountsQuery = {
    isLoading: Boolean(isLoadingFeeAccountsRaw),
    isFetching: Boolean(isFetchingFeeAccountsRaw),
    isError: Boolean(isFeeAccountsError),
    isUninitialized: Boolean(isFeeAccountsUninitialized),
    hasData: Boolean(feeAccountsData),
    skip: isAccountsQuerySkipped || !showFeeAndPerformance,
  };
  const performanceAccountsQuery = {
    isLoading: Boolean(isLoadingPerformanceAccountsRaw),
    isFetching: Boolean(isFetchingPerformanceAccountsRaw),
    isError: Boolean(isPerformanceAccountsError),
    isUninitialized: Boolean(isPerformanceAccountsUninitialized),
    hasData: Boolean(performanceAccountsData),
    skip: isAccountsQuerySkipped || !showFeeAndPerformance,
  };
  const accountQueries = [
    accountsQuery,
    feeAccountsQuery,
    performanceAccountsQuery,
  ];
  const vaultListStatus = getVaultListStatus(documentQueries);
  const showAccountsError = accountQueries.some(
    (query) => paginated && !query.skip && query.isError && !query.hasData,
  );
  const handleRetry = () => {
    const failed = getQueriesToRetry(documentQueries);
    if (failed.includes(documentsQuery)) {
      void refetchDocuments();
    }
    if (failed.includes(feeDocumentsQuery)) {
      void refetchFeeDocuments();
    }
    if (failed.includes(performanceDocumentsQuery)) {
      void refetchPerformanceDocuments();
    }
  };
  const handleAccountsRetry = () => {
    const failed = getQueriesToRetry(accountQueries);
    if (failed.includes(accountsQuery)) {
      void refetchAccounts();
    }
    if (failed.includes(feeAccountsQuery)) {
      void refetchFeeAccounts();
    }
    if (failed.includes(performanceAccountsQuery)) {
      void refetchPerformanceAccounts();
    }
  };
  const tagKey = viewingTagSelection
    ? `${viewingTagSelection.source}:${String(viewingTagSelection.tagId)}`
    : '';
  const paginationResetKey = `${String(investorId ?? '')}|${selectedAccount}|${selectedAccountIds.join(',')}|${dateFilter}|${searchTerm ?? ''}|${tagKey}`;
  const shouldShowTagEmptyState = shouldShowDocumentVaultTagEmptyState({
    hasSelectedTag: hasSelectedViewingTag,
    searchTerm,
    isLoading,
    isFetching,
    rowCount: rows.length,
    isQuerySkipped: isAdvisor && !isAccountView && !investorId,
    isAccountView,
    accountCount: vaultFileAccounts.length,
  });

  useEffect(() => {
    if (
      selectedAccount !== '' &&
      !vaultFileAccounts.some(
        (account) => String(account.WealthAccountId) === selectedAccount,
      )
    ) {
      setSelectedAccount('');
    }
  }, [vaultFileAccounts, selectedAccount]);

  useEffect(() => {
    setSelectedAccountIds((current) => {
      const next = current.filter((id) =>
        vaultFileAccounts.some((account) => account.WealthAccountId === id),
      );
      return next.length === current.length ? current : next;
    });
  }, [vaultFileAccounts]);

  // Apply filter based on searchTerm
  useEffect(() => {
    const gridApi = apiRef.current;
    if (!gridApi) {
      return;
    }

    if (searchTerm) {
      gridApi.setFilterModel({
        items: [
          {
            field: 'Name',
            operator: 'contains',
            value: searchTerm,
          },
        ],
      });
    } else {
      gridApi.setFilterModel({ items: [] });
    }
  }, [searchTerm, apiRef]);

  const feedListIsLoading =
    isAdvisorFeed &&
    (isLoadingStatements || isFetchingStatements) &&
    !statementsData;

  if (!paginated && isAdvisorFeed && isLoadingAccounts && !accountsData) {
    return (
      <Stack
        gap={1}
        px={0}
      >
        <Stack
          direction='row'
          justifyContent='flex-start'
          alignItems='center'
          spacing={0}
          sx={{
            marginTop: '40px !important',
            marginLeft: '0px !important',
            paddingLeft: '0px !important',
          }}
        >
          <Skeleton
            variant='rectangular'
            height={40}
            width={120}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 2,
            }}
          >
            <Skeleton
              variant='rectangular'
              height={40}
              width={120}
            />
            <Skeleton
              variant='rectangular'
              height={40}
              width={120}
            />
          </Box>
        </Stack>
        {skeletonRows.map((index) => (
          <Skeleton
            key={index}
            variant='rectangular'
            height={52}
          />
        ))}
      </Stack>
    );
  }

  // Loading state with skeletons
  if (
    !paginated &&
    !isAdvisorFeed &&
    (isLoading || isFetching) &&
    !statementsData
  ) {
    // Added !statementsData to prevent flickering when refetching
    return (
      <Stack
        gap={1}
        px={0}
      >
        <Stack
          direction={isMobile ? 'column' : 'row'}
          justifyContent='flex-start'
          alignItems={isMobile ? 'stretch' : 'center'}
          spacing={0}
          sx={{
            marginTop: '40px !important',
            marginLeft: '0px !important',
            paddingLeft: '0px !important',
          }}
        >
          <div />
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 2,
            }}
          >
            {!isAccountView && (
              <Skeleton
                variant='rectangular'
                height={40}
                width={isMobile ? '100%' : 120}
              />
            )}
            <Skeleton
              variant='rectangular'
              height={40}
              width={isMobile ? '100%' : 120}
            />{' '}
            {/* Skeleton for Date Dropdown */}
          </Box>
        </Stack>
        {skeletonRows.map((index) => (
          <Skeleton
            key={index}
            variant='rectangular'
            height={52}
          />
        ))}
      </Stack>
    );
  }

  if (!paginated && isAdvisorFeed && hasDocumentLoadError) {
    console.error('Error loading statements');
    return (
      <Alert severity='error'>
        {t('documentVault.feedLoadError', 'Unable to load documents.')}
      </Alert>
    );
  }

  // Error handling
  if (!paginated && !isAdvisorFeed && errorAccounts) {
    console.error('Error loading accounts:', errorAccounts);
    return <Typography color='error'>Error loading accounts.</Typography>;
  }
  if (!paginated && errorStatements) {
    console.error('Error loading statements:', errorStatements);
    return <Typography color='error'>Error loading statements.</Typography>;
  }

  const filters = (
    <>
      {isAdvisor && !isAdvisorFeed && <AdvisorTableHeader />}
      <Stack
        direction={isMobile ? 'column' : 'row'}
        justifyContent='flex-start'
        alignItems={isMobile ? 'stretch' : 'center'}
        spacing={2}
        sx={{
          p: 1,
          width: '100%',
          marginTop: '0px !important',
          marginLeft: '0px !important',
          paddingLeft: '0px !important',
        }}
      >
        {isAdvisorFeed ? (
          <Typography
            variant='h6'
            sx={{ flex: 1 }}
          >
            Recently Uploaded
          </Typography>
        ) : (
          !isMobile && <div />
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 2 : 4,
            marginLeft: '0px !important',
            justifyContent: 'space-between',
            '& .MuiFormControl-root': {
              marginLeft: '0px',
              marginRight: '0px',
              ...(isMobile && { width: '100%' }),
            },
          }}
        >
          {isAdvisorFeed ? (
            <>
              <AdvisorFeedAccountSelect
                idPrefix='statement'
                accounts={vaultFileAccounts}
                selectedAccountIds={selectedAccountIds}
                onChange={setSelectedAccountIds}
                loadFailed={!paginated && Boolean(errorAccounts)}
              />
              {showAccountsError && (
                <FormHelperText error>
                  {t('documentVault.accountsLoadError')}
                  <Button
                    size='small'
                    onClick={handleAccountsRetry}
                  >
                    {t('retry')}
                  </Button>
                </FormHelperText>
              )}
              <AdvisorFeedDateSelect
                idPrefix='statement'
                value={dateFilter}
                onChange={setDateFilter}
              />
            </>
          ) : (
            <>
              {/* Account Dropdown - shown only in general view */}
              {!isAccountView && (
                <FormControl
                  sx={{ minWidth: 180 }}
                  size='small'
                >
                  <InputLabel
                    id='statement-account-select-label'
                    shrink
                  >
                    {t('account')}
                  </InputLabel>
                  <Select
                    labelId='statement-account-select-label'
                    id='statement-account-select'
                    value={selectedAccount}
                    label='Account'
                    onChange={handleAccountChange}
                    displayEmpty
                    disabled
                  >
                    <MenuItem value=''>{t('viewingTag.allAccounts')}</MenuItem>
                    {vaultFileAccounts.map((account) => (
                      <MenuItem
                        key={account.WealthAccountId}
                        value={account.WealthAccountId}
                      >
                        {account.Name} - ({account.AccountNumber})
                      </MenuItem>
                    ))}
                  </Select>
                  {showAccountsError && (
                    <FormHelperText error>
                      {t('documentVault.accountsLoadError')}
                      <Button
                        size='small'
                        onClick={handleAccountsRetry}
                      >
                        {t('retry')}
                      </Button>
                    </FormHelperText>
                  )}
                </FormControl>
              )}

              {/* Date Interval Dropdown */}
              <FormControl
                sx={{ minWidth: 180 }}
                size='small'
              >
                <InputLabel
                  id='statement-date-interval-select-label'
                  shrink
                >
                  Date
                </InputLabel>
                <Select
                  labelId='statement-date-interval-select-label'
                  id='statement-date-interval-select'
                  value={dateFilter}
                  label='Date'
                  onChange={handleDateFilterChange}
                >
                  {statementsVaultDateFilterOptions.map((option) => (
                    <MenuItem
                      key={option.filter}
                      value={option.filter}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </Box>
      </Stack>
    </>
  );

  const grid = (
    <Box>
      <Box>
        {(isLoading || isFetching) && searchTerm && (
          <Stack
            direction='row'
            spacing={1}
            alignItems='center'
            justifyContent='center'
            sx={{ mb: 1 }}
          >
            <CircularProgress size={16} />
            <Typography variant='caption'>Loading Search...</Typography>
          </Stack>
        )}
        {shouldShowTagEmptyState ? (
          <DocumentVaultTagEmptyState />
        ) : (
          <DocVaultGrid
            columns={columns}
            rows={rows}
            loading={
              (isLoading || isFetching || isPreviewing || isDownloading) &&
              !searchTerm
            }
            checkboxSelection
            disableRowSelectionOnClick
            rowHeight={52}
            apiRef={apiRef}
            onRowAction={handleRowAction}
            investorId={investorId}
            bulkDownloadContextName='statements'
            showBulkActions={false}
            useDocuments2BulkDownload
            enableXsListView
            {...(paginated && {
              paginated: true,
              paginationResetKey,
              slots: {
                noRowsOverlay: VaultEmptyOverlay,
                noResultsOverlay: VaultEmptyOverlay,
              },
            })}
            {...(isAdvisorFeed
              ? {
                  slotProps: {
                    loadingOverlay: {
                      variant: 'linear-progress' as const,
                      noRowsVariant: 'skeleton' as const,
                    },
                  },
                  localeText: {
                    noRowsLabel: t('noDataToDisplay', 'No data to display'),
                  },
                  initialState: {
                    sorting: {
                      sortModel: [
                        { field: 'StatementDate', sort: 'desc' as const },
                      ],
                    },
                  },
                }
              : {})}
          />
        )}
      </Box>
      {!paginated && !shouldShowTagEmptyState && !feedListIsLoading && (
        <Box
          sx={{
            mt: 2,
            fontWeight: 600,
            display: 'flex',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Typography variant='subtitle1'>Total Statements</Typography>
          <Typography variant='subtitle1'>{rows.length}</Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Stack
      gap={2}
      flex={1}
      overflow='hidden'
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      {filters}
      {paginated ? (
        <VaultListState
          status={vaultListStatus}
          onRetry={handleRetry}
        >
          {grid}
        </VaultListState>
      ) : (
        grid
      )}
    </Stack>
  );
};

export default StatementsGrid;
